import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors} from '@angular/forms';
import {Subscription} from 'rxjs';
import {IpopConfiguration, LayoutPositionOption, SiteWideWidgetType} from '../../../../../model/ipop-configuration.model';
import {SiteWideUnlockForm, siteWidePageTypeOptions, validPageTypes} from './site-wide-unlock-settings';
import {
  createCustomPathForm, customPathChange, maximumCustomPathChanges, SiteWideUnlockPathForm, validCustomPathOptions
} from './site-wide-unlock-paths';
import {AccountWidgetSetting} from '../../../../../model/ipop-configuration.model';
import {
  createSiteWidePresentationForm, siteWidePresentationValues,
  siteWideTextFields, siteWideUnlockDestinationError, siteWideUrlLimit
} from './site-wide-unlock-settings';

@Component({
  selector: 'auto-site-wide-unlock',
  templateUrl: './site-wide-unlock.component.html',
  styleUrls: ['./site-wide-unlock.component.scss'],
  standalone: false
})
export class SiteWideUnlockComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: IpopConfiguration | null = null;
  @Input() accountId: number;
  @Input() isSaving = false;
  @Input() isReadOnly = true;
  @Input() websiteUrl = '';
  readonly sharedFields = siteWideTextFields.filter(field => field.name === 'ctaLabel');
  readonly bannerFields = siteWideTextFields.filter(field => ['bannerHeadline', 'bannerBody'].includes(field.name));
  readonly popupFields = siteWideTextFields.filter(field => ['popupHeadline', 'popupBody'].includes(field.name));
  readonly formFields = siteWideTextFields.filter(field => ['formTitle', 'formSubtitle'].includes(field.name));
  readonly navigationFields = siteWideTextFields.filter(field => field.name === 'navigationLabel');
  useInheritedDestination = true;
  defaultDestinationUrl = '';
  destinationError: string | null = null;
  readonly urlLimit = siteWideUrlLimit;
  @Output() formGroupInitialized = new EventEmitter<SiteWideUnlockForm>();

  readonly form: SiteWideUnlockForm = new FormGroup({
    siteWideUnlockEnabled: new FormControl(false, {nonNullable: true}),
    bannerEnabled: new FormControl(false, {nonNullable: true}),
    unlockPopupEnabled: new FormControl(false, {nonNullable: true}),
    navigationPopupEnabled: new FormControl(false, {nonNullable: true}),
    unlockDesktop: new FormControl<number | null>(null),
    navigationDesktop: new FormControl<number | null>(null),
    presentation: createSiteWidePresentationForm(),
    pageTypes: new FormGroup({
      homepage: new FormControl(false, {nonNullable: true}),
      vdp: new FormControl(false, {nonNullable: true}),
      srp: new FormControl(false, {nonNullable: true}),
      dt_landing_page: new FormControl(false, {nonNullable: true})
    }),
    customPaths: new FormArray<SiteWideUnlockPathForm>([]),
    saveError: new FormControl('', {nonNullable: true}),
    requiresReload: new FormControl(false, {nonNullable: true})
  });
  readonly pageTypeOptions = siteWidePageTypeOptions;
  readonly maximumPathChanges = maximumCustomPathChanges;
  desktopOptions: LayoutPositionOption[] = [];
  settingsReady = false;
  private changes: Subscription;

  /** Shares one typed draft with the owning save shell and its unsaved-change guard. */
  ngOnInit(): void
  {
    this.form.setValidators(() => this.validateSettings());
    this.changes = this.form.valueChanges.subscribe(() => {
      this.clearSaveErrors();
      this.updateDisabledState();
    });
    this.updateDisabledState();
    this.formGroupInitialized.emit(this.form);
  }

  /** Clears the previous account before accepting a matching, complete response. */
  ngOnChanges(changes: SimpleChanges): void
  {
    if (changes.accountId || (changes.data && !this.data)) {
      this.settingsReady = false;
      this.desktopOptions = [];
      this.form.controls.customPaths.clear({emitEvent: false});
      this.form.reset({}, {emitEvent: false});
    }
    if ((changes.data || changes.accountId) && this.data?.accountId === this.accountId) {
      this.loadSettings(this.data);
    }
    this.updateDisabledState();
  }

  /** Releases the form subscription when changing settings screens. */
  ngOnDestroy(): void
  {
    this.changes?.unsubscribe();
  }

  /** Loads normalized saved values without opting discovered paths into the feature. */
  private loadSettings(data: IpopConfiguration): void
  {
    this.desktopOptions = [];
    if (Array.isArray(data.layoutPositionOptions)) {
      this.desktopOptions = data.layoutPositionOptions;
    }
    this.settingsReady = typeof data.siteWideUnlockEnabled === 'boolean'
      && Array.isArray(data.widgetSettings) && this.hasValidOptions()
      && validPageTypes(data.siteWideUnlockPageTypes) && validCustomPathOptions(data.siteWideUnlockCustomPaths);
    this.form.controls.customPaths.clear({emitEvent: false});
    this.resetWidgetSettings(data);
    if (this.settingsReady) {
      this.form.controls.pageTypes.reset(data.siteWideUnlockPageTypes, {emitEvent: false});
      for (const path of data.siteWideUnlockCustomPaths) {
        this.form.controls.customPaths.push(createCustomPathForm(path), {emitEvent: false});
      }
    }
    this.form.markAsPristine();
  }

  /** Retains saved role and placement choices even when master enablement is off. */
  private resetWidgetSettings(data: IpopConfiguration): void
  {
    const banner = this.findWidget(data, 'site_wide_unlock_banner');
    const popup = this.findWidget(data, 'site_wide_unlock_popup');
    const navigation = this.findWidget(data, 'site_wide_unlock_navigation');
    this.form.reset({
      siteWideUnlockEnabled: data.siteWideUnlockEnabled ?? false,
      bannerEnabled: banner?.enabled ?? false,
      unlockPopupEnabled: popup?.enabled ?? false,
      navigationPopupEnabled: navigation?.enabled ?? false,
      unlockDesktop: popup?.positionLayoutId ?? null,
      navigationDesktop: navigation?.positionLayoutId ?? null,
      presentation: siteWidePresentationValues(data)
    }, {emitEvent: false});
    this.useInheritedDestination = !this.form.controls.presentation.controls.destinationUrlOverride.value.trim();
  }

  /** Adds an unsaved inclusion; only a successful readback can supply its account identity. */
  addCustomPath(): void
  {
    if (this.form.controls.pageTypes.enabled && this.changedPathCount() < maximumCustomPathChanges) {
      this.form.controls.customPaths.push(createCustomPathForm());
      this.form.markAsDirty();
    }
  }

  /** Discards a draft without asking the API to delete an existing route. */
  removeNewPath(row: SiteWideUnlockPathForm): void
  {
    const index = this.form.controls.customPaths.controls.indexOf(row);
    if (this.form.controls.pageTypes.enabled && row.controls.saved.value === null && index >= 0) {
      this.form.controls.customPaths.removeAt(index);
      this.form.markAsDirty();
    }
  }

  /** Deselection removes only the saved inclusion, so an unfinished replacement is discarded. */
  toggleCustomPath(row: SiteWideUnlockPathForm): void
  {
    const saved = row.controls.saved.value;
    if (!this.isReadOnly && row.controls.selected.enabled && !row.controls.selected.value && saved !== null) {
      row.controls.path.setValue(saved.path);
    }
  }

  /** Counts operations rather than all options, matching the API's batch limit. */
  changedPathCount(): number
  {
    const count = this.form.controls.customPaths.controls.filter(row => customPathChange(row) !== null).length;

    return count;
  }

  /** Clears stale rejection messages when the user corrects a draft. */
  private clearSaveErrors(): void
  {
    if (!this.form.controls.requiresReload.value) {
      this.form.controls.saveError.setValue('', {emitEvent: false});
      for (const row of this.form.controls.customPaths.controls) {
        row.controls.saveError.setValue('', {emitEvent: false});
      }
    }
  }

  /** Finds persisted enablement and placement for one widget type. */
  private findWidget(data: IpopConfiguration, widgetType: SiteWideWidgetType): AccountWidgetSetting | undefined
  {
    const widget = data.widgetSettings?.find(row => row.widgetType === widgetType);

    return widget;
  }

  /** Requires usable and unique placement IDs before any Sitewide save. */
  private hasValidOptions(): boolean
  {
    const valid = this.desktopOptions.length > 0
      && this.desktopOptions.every(option => option !== null && typeof option === 'object'
        && Number.isSafeInteger(option.id) && option.id > 0
        && typeof option.label === 'string' && option.label.trim().length > 0)
      && new Set(this.desktopOptions.map(option => option.id)).size === this.desktopOptions.length;

    return valid;
  }

  /** Blocks incomplete loads, invalid placements, oversized batches, and unconfirmed saves. */
  private validateSettings(): ValidationErrors | null
  {
    let errors: ValidationErrors | null = null;
    const values = this.form.getRawValue();
    const placementsValid = !values.siteWideUnlockEnabled || (
      this.hasValidSelection(values.unlockDesktop, values.unlockPopupEnabled)
      && this.hasValidSelection(values.navigationDesktop, values.navigationPopupEnabled)
    );
    if (!this.settingsReady || this.form.controls.requiresReload.value) {
      errors = {settingsUnavailable: true};
    }
    if (!placementsValid) {
      errors = {...errors, siteWidePlacement: true};
    }
    if (this.changedPathCount() > maximumCustomPathChanges) {
      errors = {...errors, tooManyPaths: true};
    }

    this.destinationError = siteWideUnlockDestinationError(this.data, this.form, this.websiteUrl);
    if (this.destinationError !== null) {
      errors = {...errors, siteWideDestination: true};
    }

    return errors;
  }

  /** Disabled roles may retain a known placement or no placement. */
  private hasValidSelection(id: number | null, enabled: boolean): boolean
  {
    let valid = !enabled && id === null;
    if (id !== null) {
      valid = this.desktopOptions.some(option => option?.id === id);
    }

    return valid;
  }

  /** Allows targeting to be prepared while master is off; only delivery depends on master. */
  private updateDisabledState(): void
  {
    const ready = !this.isReadOnly && this.settingsReady && this.data?.accountId === this.accountId
      && this.data?.canConfigureSiteWideUnlock === true && !this.isSaving && !this.form.controls.requiresReload.value;
    const enabled = ready && this.form.controls.siteWideUnlockEnabled.value;
    this.setDisabled(this.form.controls.siteWideUnlockEnabled, !ready);
    for (const name of ['bannerEnabled', 'unlockPopupEnabled', 'navigationPopupEnabled'] as const) {
      this.setDisabled(this.form.controls[name], !enabled);
    }
    this.setDisabled(this.form.controls.unlockDesktop, !enabled || !this.form.controls.unlockPopupEnabled.value);
    this.setDisabled(this.form.controls.navigationDesktop, !enabled || !this.form.controls.navigationPopupEnabled.value);
    this.setDisabled(this.form.controls.presentation, !enabled);
    this.updateSectionDisabledState(enabled);
    this.defaultDestinationUrl = this.data?.siteWideUnlockPresentation?.defaultUrl ?? '';
    this.setDisabled(this.form.controls.pageTypes, !ready);
    for (const row of this.form.controls.customPaths.controls) {
      this.setDisabled(row.controls.selected, !ready);
      this.setDisabled(row.controls.path, !ready || !row.controls.selected.value);
    }
    this.form.updateValueAndValidity({emitEvent: false});
  }

  /** Keeps each widget's text and destination controls aligned with its enablement toggle. */
  private updateSectionDisabledState(enabled: boolean): void
  {
    const controls = this.form.controls.presentation.controls;
    for (const field of this.bannerFields) {
      this.setDisabled(controls[field.name], !enabled || !this.form.controls.bannerEnabled.value);
    }
    for (const field of this.popupFields) {
      this.setDisabled(controls[field.name], !enabled || !this.form.controls.unlockPopupEnabled.value);
    }
    const navigationDisabled = !enabled || !this.form.controls.navigationPopupEnabled.value;
    for (const field of this.navigationFields) {
      this.setDisabled(controls[field.name], navigationDisabled);
    }
    this.setDisabled(controls.destinationUrlOverride, navigationDisabled);
  }

  /** Keeps the native picker and editable hex input synchronized through one form control. */
  setColor(name: 'primaryColor' | 'secondaryColor', value: string): void
  {
    const control = this.form.controls.presentation.controls[name];
    control.setValue(value);
    control.markAsDirty();
    this.form.updateValueAndValidity();
  }

  /** Maps the inheritance checkbox to the existing nullable override without changing the API contract. */
  setDestinationInheritance(inherited: boolean): void
  {
    const control = this.form.controls.presentation.controls.destinationUrlOverride;
    this.useInheritedDestination = inherited;
    let override = this.defaultDestinationUrl;
    if (inherited) {
      override = '';
    }
    control.setValue(override);
    control.markAsDirty();
    this.form.updateValueAndValidity();
  }

  /** Updates availability without recursively emitting form changes. */
  private setDisabled(control: AbstractControl, disabled: boolean): void
  {
    if (disabled && control.enabled) {
      control.disable({emitEvent: false});
    }
    if (!disabled && control.disabled) {
      control.enable({emitEvent: false});
    }
  }
}
